import { useState } from 'react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog';
import { Star, MessageSquare } from 'lucide-react';
import { reviewAPI } from '../lib/api';
import { reviewerRoles } from '../lib/utils';

const MIN_WORDS = 30;
const MAX_CHARS = 1000;

const ReviewForm = ({ targetType, targetId, targetName, onSuccess, userRole }) => {
  const [open, setOpen] = useState(false);
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [reviewText, setReviewText] = useState('');
  const [reviewerType, setReviewerType] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Calculate word count
  const wordCount = reviewText.trim() ? reviewText.trim().split(/\s+/).length : 0;
  const isWordCountValid = wordCount >= MIN_WORDS;
  const charCount = reviewText.length;

  // Determine color based on word count
  const getWordCountColor = () => {
    if (wordCount === 0) return 'text-muted-foreground';
    if (wordCount < MIN_WORDS * 0.5) return 'text-destructive';
    if (wordCount < MIN_WORDS) return 'text-amber-500';
    return 'text-primary';
  };

  const handleSubmit = async () => {
    if (!rating || !reviewText.trim() || !reviewerType || !isWordCountValid) {
      setError('Please fill in all fields and ensure your review meets the minimum word count.');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      await reviewAPI.create({
        target_type: targetType,
        target_id: targetId,
        reviewer_type: reviewerType,
        rating,
        review_text: reviewText.trim(),
      });
      
      setOpen(false);
      setRating(0);
      setReviewText('');
      setReviewerType('');
      onSuccess?.();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to submit review. Please try again.');
    }
    
    setSubmitting(false);
  };

  const handleClose = () => {
    setOpen(false);
    setError('');
  };

  // Determine available reviewer types based on user role
  const getReviewerTypeOptions = () => {
    if (userRole === 'reviewer') {
      return reviewerRoles;
    }
    // For facilitators, they can only review camps as a professional
    if (userRole === 'facilitator') {
      return [{ value: 'professional', label: 'Professional / Industry' }];
    }
    // Default fallback
    return reviewerRoles;
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button 
          variant="outline" 
          className="rounded-full"
          data-testid="write-review-btn"
        >
          <MessageSquare className="w-4 h-4 mr-2" />
          Write a Review
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Write a Review</DialogTitle>
          <DialogDescription>
            Share your experience with {targetName}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {error && (
            <div className="p-3 bg-destructive/10 text-destructive rounded-xl text-sm" data-testid="review-form-error">
              {error}
            </div>
          )}

          {/* Rating */}
          <div className="space-y-2">
            <Label>Rating *</Label>
            <div className="flex gap-1" data-testid="rating-stars">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setRating(star)}
                  onMouseEnter={() => setHoverRating(star)}
                  onMouseLeave={() => setHoverRating(0)}
                  className="p-1 transition-transform hover:scale-110"
                  data-testid={`rating-star-${star}`}
                >
                  <Star
                    className={`w-8 h-8 transition-colors ${
                      star <= (hoverRating || rating)
                        ? 'text-yellow-500 fill-yellow-500'
                        : 'text-muted stroke-muted-foreground'
                    }`}
                  />
                </button>
              ))}
            </div>
            {rating > 0 && (
              <p className="text-sm text-muted-foreground">
                {rating === 1 && 'Poor'}
                {rating === 2 && 'Fair'}
                {rating === 3 && 'Good'}
                {rating === 4 && 'Very Good'}
                {rating === 5 && 'Excellent'}
              </p>
            )}
          </div>

          {/* Reviewer Type */}
          <div className="space-y-2">
            <Label>I am a... *</Label>
            <Select value={reviewerType} onValueChange={setReviewerType}>
              <SelectTrigger data-testid="reviewer-type-select">
                <SelectValue placeholder="Select your role" />
              </SelectTrigger>
              <SelectContent>
                {getReviewerTypeOptions().map((role) => (
                  <SelectItem key={role.value} value={role.value}>
                    {role.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Review Text with Word Counter */}
          <div className="space-y-2">
            <Label>Your Review *</Label>
            <Textarea
              placeholder="Share your experience... (minimum 30 words)"
              value={reviewText}
              onChange={(e) => setReviewText(e.target.value.slice(0, MAX_CHARS))}
              rows={5}
              className="resize-none"
              data-testid="review-text-input"
            />
            <div className="flex justify-between items-center text-sm">
              <div className={`font-medium ${getWordCountColor()}`} data-testid="word-counter">
                {wordCount} / {MIN_WORDS} words minimum
                {isWordCountValid && <span className="ml-1">✓</span>}
              </div>
              <div className="text-muted-foreground">
                {charCount} / {MAX_CHARS} characters
              </div>
            </div>
            {!isWordCountValid && wordCount > 0 && (
              <p className="text-xs text-amber-500">
                {MIN_WORDS - wordCount} more word{MIN_WORDS - wordCount !== 1 ? 's' : ''} needed
              </p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit}
            disabled={submitting || !rating || !reviewerType || !isWordCountValid}
            data-testid="submit-review-btn"
          >
            {submitting ? 'Submitting...' : 'Submit Review'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ReviewForm;
